// reducers/support-chat-slice.js
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { getQueryString } from 'utils/filters-util';
import { fetchChatConversations, fetchChatProfiles, getLeadConversationApi } from '../../api/support-chat-api';
import { getErrorString } from '../../utils';

const initialState = {
  profiles: [],
  profilesInfo: {},
  profileInfoLoading: false,
  loading: true,
  loadingMore: false,
  hasMore: true,
  isRefreshing: false,
  pagination: {},
  error: null,
  lastFetchedAt: null,
  hasNewerMessages: false,
};

export const fetchUserProfiles = createAsyncThunk(
  'support/chat/fetchUserProfiles',
  async (userId, { rejectWithValue }) => {
    try {
      const res = await fetchChatProfiles(userId);
      return res;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchProfiles = createAsyncThunk('support/chat/fetchProfiles', async (paramsObj, { rejectWithValue }) => {
  try {
    const updatedParams = {
      page: 1,
      per_page: '10',
      'q[s]': 'last_message_at desc',
      'q[type_slug_eq]': 'crm',
      ...paramsObj,
    };
    const paramsStr = getQueryString(updatedParams);
    const res = await fetchChatConversations(paramsStr);
    return res;
  } catch (error) {
    toast.error(getErrorString(error));
    return rejectWithValue(error.message);
  }
});

export const fetchProfileInfo = createAsyncThunk(
  'support/chat/fetchProfileInfo',
  async (profileId, { rejectWithValue }) => {
    try {
      const updatedParams = {
        page: 1,
        per_page: '10',
        'q[type_slug_eq]': 'crm',
        'q[id_eq]': profileId,
      };
      const paramsStr = getQueryString(updatedParams);
      const res = await fetchChatConversations(paramsStr);
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
      return rejectWithValue(error.message);
    }
  },
);

export const fetchLeadProfileInfo = createAsyncThunk(
  'lead/detail/fetchProfileInfo',
  async (leadId, { rejectWithValue }) => {
    try {
      const res = await getLeadConversationApi(leadId);
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
      return rejectWithValue(error.message);
    }
  },
);

// Thunk for loading more
export const loadMoreProfiles = createAsyncThunk(
  'support/chat/loadMoreProfiles',
  async (paramsObj, { getState, rejectWithValue }) => {
    try {
      const state = getState().supportChatData;
      const nextPage = state.pagination.page + 1;

      const updatedParams = {
        page: nextPage,
        per_page: '10',
        'q[s]': 'last_message_at desc',
        'q[type_slug_eq]': 'crm',
        ...paramsObj,
      };
      const paramsStr = getQueryString(updatedParams);

      const res = await fetchChatConversations(paramsStr);
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
      return rejectWithValue(error.message);
    }
  },
);

export const refreshProfiles = createAsyncThunk(
  'support/chat/refreshProfiles',
  async (paramsObj, { rejectWithValue }) => {
    try {
      const updatedParams = {
        page: 1,
        per_page: '10',
        'q[s]': 'last_message_at desc',
        'q[type_slug_eq]': 'crm',
        ...paramsObj,
      };
      const paramsStr = getQueryString(updatedParams);

      const res = await fetchChatConversations(paramsStr);
      return res;
    } catch (error) {
      toast.error(getErrorString(error));
      return rejectWithValue(error.message);
    }
  },
);

const SupportChatSlice = createSlice({
  name: 'supportchat',
  initialState,
  reducers: {
    setProfilesInfo: (state, { payload }) => {
      state.profilesInfo[payload?.id] = {
        ...state.profilesInfo[payload?.id],
        ...payload,
      };
    },
    setProfileInfoShortDetails: (state, { payload }) => {
      state.profilesInfo[payload?.id] = {
        ...state.profilesInfo[payload?.id],
        short_details: payload?.short_details ?? null,
      };
    },
    resetProfiles: (state) => {
      state.profiles = [];
      state.pagination = {};
      state.hasMore = true;
      state.loading = true;
      state.loadingMore = false;
      state.error = null;
    },
    updateProfileInList: (state, { payload }) => {
      const index = state.profiles.findIndex((p) => p.id === payload.id);
      if (index !== -1) {
        state.profiles[index] = { ...state.profiles[index], ...payload };
      }
    },
    removeProfileFromList: (state, { payload }) => {
      state.profiles = state.profiles.filter((p) => p.id !== payload.id);
      state.pagination.count = Math.max(0, state.pagination.count - 1);
    },
    setHasNewerMessages: (state, { payload }) => {
      state.hasNewerMessages = payload;
    },

    resetNewerMessages: (state) => {
      state.hasNewerMessages = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initial fetch
      .addCase(fetchProfiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfiles.fulfilled, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        state.profiles = action.payload?.data || [];
        state.pagination = action.payload?.meta?.pagination || {};
        state.hasMore = (action.payload?.meta?.pagination?.pages || 1) > 1;
        state.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchProfiles.rejected, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        state.error = action.payload;
      })

      .addCase(loadMoreProfiles.pending, (state) => {
        state.loadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreProfiles.fulfilled, (state, action) => {
        state.loadingMore = false;
        const newProfiles = action.payload?.data || [];
        state.profiles = [...state.profiles, ...newProfiles];
        state.pagination = action.payload?.meta?.pagination || state.pagination;
        state.hasMore = state.pagination.page < state.pagination.pages;
        state.lastFetchedAt = new Date().toISOString();
      })
      .addCase(loadMoreProfiles.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.payload;
      })

      // Refresh
      .addCase(refreshProfiles.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(refreshProfiles.fulfilled, (state, action) => {
        state.isRefreshing = false;
        state.profiles = action.payload?.data || [];
        state.pagination = action.payload?.meta?.pagination || {
          page: 1,
          pages: 1,
          count: 0,
          items: 30,
        };
        state.hasMore = (action.payload?.meta?.pagination?.pages || 1) > 1;
        state.lastFetchedAt = new Date().toISOString();
      })
      .addCase(refreshProfiles.rejected, (state, action) => {
        state.isRefreshing = false;
        state.error = action.payload;
      })

      //profile Info
      .addCase(fetchProfileInfo.pending, (state) => {
        state.profileInfoLoading = true;
      })
      .addCase(fetchProfileInfo.fulfilled, (state, action) => {
        state.profileInfoLoading = false;
        const profile = action.payload?.data?.[0];
        if (profile?.id) {
          state.profilesInfo[profile.id] = { ...profile };
        }
      })
      .addCase(fetchProfileInfo.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(getErrorString(action.payload));
      })

      //profile info lead detail
      .addCase(fetchLeadProfileInfo.pending, (state) => {
        state.profileInfoLoading = true;
      })
      .addCase(fetchLeadProfileInfo.fulfilled, (state, action) => {
        state.profileInfoLoading = false;
        const profile = action.payload?.data;
        if (profile?.id) {
          state.profilesInfo[profile.id] = { ...profile };
        }
      })
      .addCase(fetchLeadProfileInfo.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(getErrorString(action.payload));
      })

      //User Profile
      .addCase(fetchUserProfiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfiles.fulfilled, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        state.profiles = action.payload?.data ? [{ ...action.payload.data }] : [];
        state.pagination = action.payload?.meta?.pagination || {};
        state.hasMore = (action.payload?.meta?.pagination?.pages || 1) > 1;
        state.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchUserProfiles.rejected, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        state.error = action.payload;
      });
  },
});

export const {
  setProfilesInfo,
  setProfileInfoShortDetails,
  resetProfiles,
  updateProfileInList,
  removeProfileFromList,
  resetNewerMessages,
  setHasNewerMessages,
} = SupportChatSlice.actions;

export default SupportChatSlice.reducer;
